import React, { useEffect, useState } from 'react';
import { Badge, Button, Card, Col, Modal, Row, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'helpers/api';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import AdvanceTableFooter from 'components/common/advance-table/AdvanceTableFooter';
import AdvanceTableSearchBox from 'components/common/advance-table/AdvanceTableSearchBox';
import CommonBreadCrumb from 'components/commonbreadcrumb/CommonBreadCrumb';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import paths from 'routes/paths';
import DayProgramForm from './DayProgramForm';

const getDayProgramsFromResponse = data => {
  const candidates = [data?.data?.day_programs, data?.day_programs, data?.data, data];
  return candidates.find(Array.isArray) || [];
};

const getStatusLabel = isActive => {
  if (isActive === 1 || isActive === true || isActive === '1' || isActive === 'active') {
    return 'active';
  }

  return 'inactive';
};

const DayProgramList = () => {
  const [dayPrograms, setDayPrograms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [selectedDayProgram, setSelectedDayProgram] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchDayPrograms = async () => {
    setLoading(true);
    try {
      const tokenStr = localStorage.getItem('accessToken');
      const { data } = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/day-programs/list`,
        { headers: { Authorization: `Bearer ${tokenStr}` } }
      );

      const resultsStorage = [];
      resultsStorage.push(...data.data.filter(dayProgram => Number(dayProgram.is_active) === 1).map(dayProgram => ({
        key: dayProgram.name,
        value: dayProgram.id
      })));
      localStorage.setItem('dayProgramOptions', JSON.stringify(resultsStorage));

      const results = getDayProgramsFromResponse(data)
        .map(dayProgram => ({
          id: dayProgram.id,
          name: dayProgram.name || '',
          description: dayProgram.description || '',
          patient_count: dayProgram.patient_count || 0,
          user_count: dayProgram.user_count || 0,
          address1: dayProgram.address1 || '',
          address2: dayProgram.address2 || '',
          city: dayProgram.city || '',
          state: dayProgram.state || '',
          zip: dayProgram.zip || '',
          phone: dayProgram.phone || '',
          email: dayProgram.email || '',
          is_active:
            getStatusLabel(
              dayProgram.is_active === undefined ? dayProgram.status : dayProgram.is_active
            ) === 'active'
        }))
        .sort((a, b) => a.name.localeCompare(b.name));

      setDayPrograms(results);
    } catch (error) {
      setDayPrograms([]);
      toast.error('Unable to load day programs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDayPrograms();
  }, []);

  const handleCreate = () => {
    setSelectedDayProgram(null);
    setShowForm(true);
  };

  const handleEdit = dayProgram => {
    setSelectedDayProgram(dayProgram);
    setShowForm(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget?.id) return;

    setDeleting(true);
    try {
      const tokenStr = localStorage.getItem('accessToken');
      await axios.delete(
        `${process.env.REACT_APP_SERVER_URL}/day-programs/delete/${deleteTarget.id}`,
        { headers: { Authorization: `Bearer ${tokenStr}` } }
      );

      toast.success('Day Program deleted successfully');
      setDeleteTarget(null);
      fetchDayPrograms();
    } catch (error) {
      toast.error('Unable to delete day program');
    } finally {
      setDeleting(false);
    }
  };

  const columns = [
    {
      accessorKey: 'name',
      header: 'Name',
      meta: {
        headerProps: { className: 'text-900 text-start' },
        cellProps: { className: 'text-start' }
      },
      cell: ({ row: { original } }) => (
        <Link to={paths.adminDayProgramDetails.replace(':id', original.id)}>
          {original.name}
        </Link>
      )
    },
    {
      accessorKey: 'description',
      header: 'Description',
      meta: {
        headerProps: { className: 'text-900 text-start' },
        cellProps: { className: 'text-start' }
      }
    },
    {
      accessorKey: 'patient_count',
      header: 'Patient Count',
      meta: {
        headerProps: { className: 'text-900 text-center' },
        cellProps: { className: 'text-center' }
      }
    },
    {
      accessorKey: 'user_count',
      header: 'User Count',
      meta: {
        headerProps: { className: 'text-900 text-center' },
        cellProps: { className: 'text-center' }
      }
    },
    {
      accessorKey: 'status',
      header: 'Status',
      meta: {
        headerProps: { className: 'text-900 text-center' },
        cellProps: { className: 'text-center' }
      },
      cell: ({ row: { original } }) => {
        const status = getStatusLabel(original.is_active);
        return (
          <Badge bg={status === 'active' ? 'success' : 'secondary'}>
            {status}
          </Badge>
        );
      }
    },
    {
      accessorKey: 'actions',
      header: 'Actions',
      enableSorting: false,
      meta: {
        headerProps: { className: 'text-900 text-center' },
        cellProps: { className: 'text-center' }
      },
      cell: ({ row: { original } }) => (
        <>
          <Button
            variant="link"
            size="sm"
            className="p-0 me-3"
            onClick={() => handleEdit(original)}
          >
            <FontAwesomeIcon icon={['far', 'edit']} className="me-1" />
            Edit
          </Button>
          {/* <Button
            variant="link"
            size="sm"
            className="p-0 text-danger"
            onClick={() => setDeleteTarget(original)}
          >
            <FontAwesomeIcon icon="trash-alt" className="me-1" />
            Delete
          </Button> */}
        </>
      )
    }
  ];

  const table = useAdvanceTable({
    data: dayPrograms,
    columns,
    selection: false,
    sortable: true,
    pagination: true,
    perPage: 10
  });

  const breadcrumbItems = [
    { name: 'Dashboard', href: '/main', isActive: false },
    { name: 'Day Programs', href: paths.adminDayPrograms, isActive: true }
  ];

  return (
    <>
      <CommonBreadCrumb breadcrumbItems={breadcrumbItems} />
      <Card className="mt-3">
        <Card.Header>
          <Row className="align-items-center g-2">
            <Col>
              <h5 className="mb-0">Day Programs</h5>
            </Col>
            <Col xs="auto">
              <Button variant="primary" size="sm" onClick={handleCreate}>
                <FontAwesomeIcon icon="plus" className="me-2" />
                Create Day Program
              </Button>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body>
          <AdvanceTableProvider {...table}>
            <Row className="mb-3">
              <Col xs="12" md="4" className="ms-auto">
                <AdvanceTableSearchBox placeholder="Search..." />
              </Col>
            </Row>
            {loading ? (
              <div className="text-center py-4">
                <Spinner animation="border" />
              </div>
            ) : dayPrograms.length > 0 ? (
              <>
                <AdvanceTable
                  headerClassName="bg-200 text-nowrap align-middle text-center"
                  rowClassName="text-nowrap align-middle"
                  tableProps={{
                    size: 'sm',
                    striped: true,
                    className: 'fs-10 mb-0 overflow-hidden'
                  }}
                />
                <div className="mt-3">
                  <AdvanceTableFooter rowInfo navButtons rowsPerPageSelection />
                </div>
              </>
            ) : (
              <div className="text-center text-danger fw-bold py-4">
                There is no data available in the table
              </div>
            )}
          </AdvanceTableProvider>
        </Card.Body>
      </Card>

      <DayProgramForm
        dayProgram={selectedDayProgram}
        show={showForm}
        setShow={setShowForm}
        onSaved={fetchDayPrograms}
      />

      <Modal show={!!deleteTarget} onHide={() => setDeleteTarget(null)}>
        <Modal.Header closeButton>
          <Modal.Title as="h5">Delete Day Program</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete {deleteTarget?.name || 'this day program'}?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeleteTarget(null)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete} disabled={deleting}>
            {deleting && <Spinner animation="border" size="sm" className="me-2" />}
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default DayProgramList;
